import {
  SKU_INVALID_CHAR_PATTERN,
  SKU_INVALID_CHAR_PATTERN_GLOBAL,
  SKU_MAX_LENGTH,
  SKU_TOO_LONG_ERROR,
  SUFFIXED_SKU_PATTERN,
  skuInvalidCharError,
} from '@/constants'

/**
 * SKU character rules.
 *
 * SKUs may contain ONLY `A–Z`, `a–z`, `0–9`, `-`, `_` and `.`, up to
 * `SKU_MAX_LENGTH` characters — the set eBay carries safely.
 * Everything else — spaces, `#`, `/`, `%`, `&`, symbols, emoji,
 * accented/non-ASCII letters — is invalid: hard-rejected at entry (never
 * allowed into the field) and blocking for listing.
 *
 * Case is preserved as entered (lowercase is valid); empty is out of scope for
 * this rule — required/optional behaviour is governed elsewhere.
 *
 * Patterns + error strings live in `@/constants` (settings).
 */

/**
 * True when `value` is a valid SKU. Empty/nullish is considered valid here —
 * this only governs character CONTENT, not whether a SKU is required and not
 * its length (length is an input-time rule; a stored value can be longer than
 * the current cap because the API appends `-<base36 card id>`, and refusing to
 * list over that would punish sellers for a suffix they never typed).
 *
 * The whole value is judged. It used to check only the part before the first
 * hyphen, because a hyphen could only have come from the API's own suffix and
 * flagging that suffix blocked listing on cards whose SKUs were fine. Now that
 * hyphens are legal the carve-out is unnecessary AND wrong: the suffix is
 * `-[0-9a-z]+`, which passes on its own merits, while the old scope let a
 * legacy `AB-CD EF` list on the strength of `AB`.
 */
export const isValidSku = (value) => !SKU_INVALID_CHAR_PATTERN.test(String(value ?? ''))

/**
 * The first character of `value` that isn't allowed, or '' when all of them
 * are. What the inline error points at: "remove the bad character" is only
 * actionable if the reader can tell which one, and a pasted string can bury it.
 */
export const firstInvalidSkuChar = (value) =>
  String(value ?? '').match(SKU_INVALID_CHAR_PATTERN)?.[0] ?? ''

/**
 * The error a raw input value earns, or '' when it is fine.
 *
 * Character first, then length: a value that is both too long and contains a
 * space gets the space named, because cleaning it is the step that has to
 * happen either way.
 */
export const skuInputError = (value) => {
  const raw = String(value ?? '')
  const invalid = firstInvalidSkuChar(raw)

  if (invalid) return skuInvalidCharError(invalid)
  if (raw.length > SKU_MAX_LENGTH) return SKU_TOO_LONG_ERROR

  return ''
}

/** Strip every disallowed character and clamp to the length cap. */
export const sanitizeSku = (value) =>
  String(value ?? '').replace(SKU_INVALID_CHAR_PATTERN_GLOBAL, '').slice(0, SKU_MAX_LENGTH)

/**
 * Paste guard for the SKU inputs.
 *
 * The `input` handlers already strip disallowed characters, but that lets the
 * raw clipboard text land in the field for a frame first, and (with `v-model`)
 * pushes it through the model on its way out. This cancels the native paste
 * and inserts the sanitized text at the caret itself, so a disallowed
 * character is never in the field at all — the same hard-reject the keydown
 * guard gives typing.
 *
 * `setRangeText` bypasses `maxlength`, so the insertion is clamped to whatever
 * room the field has left.
 *
 * A synthetic `input` event is dispatched afterwards so the existing listeners
 * (`v-model`, the debounced store update) run exactly as they do for typing —
 * only with an already-clean value.
 *
 * Returns the error the clipboard earned ('' when it was clean), so the caller
 * can flash it. Paste is where the length rule actually bites: `maxlength`
 * stops typing past the cap on its own, but a pasted string can arrive far too
 * long, and being clamped without a word is the silent rewrite this whole path
 * exists to avoid.
 */
export const pasteSanitizedSku = (event) => {
  const input = event.target

  event.preventDefault()

  const pasted = event.clipboardData?.getData('text') ?? ''
  const cleaned = sanitizeSku(pasted)

  const start = input.selectionStart ?? input.value.length
  const end = input.selectionEnd ?? input.value.length
  const maxLength = input.maxLength > 0 ? input.maxLength : SKU_MAX_LENGTH
  const room = Math.max(maxLength - (input.value.length - (end - start)), 0)
  const inserted = cleaned.slice(0, room)

  input.setRangeText(inserted, start, end, 'end')
  input.dispatchEvent(new Event('input', { bubbles: true }))

  // The character error wins when there is one; otherwise the only way the
  // paste changed shape is that it didn't fit.
  return skuInputError(pasted) || (inserted.length < cleaned.length ? SKU_TOO_LONG_ERROR : '')
}

/**
 * Read-only base-36 card id that Batch Details shows after the SKU
 * (`47690 - 13oej6`) so the seller can see the full SKU that reaches the
 * marketplaces.
 *
 * The API now bakes that suffix into `sku` itself for new batches, so appending
 * it unconditionally rendered `47690-13oej6 - 13oej6`. Returns '' whenever the
 * value already carries a suffix, so it is never doubled.
 *
 * Detection is by shape (`SUFFIXED_SKU_PATTERN`), not by comparing to this
 * card's own id: the API has emitted SKUs carrying a neighbouring card's id
 * (`47696-13oekj` on the card whose id is `13oeki`), and re-appending our
 * locally computed id there reads as a second suffix just the same.
 */
export const skuIdSuffix = (sku, id) => {
  if (id == null) return ''

  const value = String(sku ?? '').trim()

  if (value && SUFFIXED_SKU_PATTERN.test(value)) return ''

  return id.toString(36)
}
